import api from './api';
import type {
  Lead,
  LeadCreateRequest,
  LeadFilters,
  LeadActivity,
  LeadSource,
  LeadCampaign,
  PaginatedResponse,
} from '@/types';

/**
 * #273 -- the lead write payload and the convert responses.
 *
 * Declared HERE rather than in `types/index.ts` because that file is owned by
 * another workstream this batch. `LeadCreateRequest` there is the old,
 * six-field shape; the Contacts-shaped form sends this instead.
 */

/**
 * The Contacts-form fields that have no column of their own on `Lead`. The
 * backend stores them under `lead_data["contact"]` and copies them onto the
 * Customer row at conversion (see `apps/leads/conversion.py`).
 */
export interface LeadContactDetails {
  customer_type?: string;
  segment?: string;
  middle_name?: string;
  date_of_birth?: string | null;
  gender?: string;
  marital_status?: string;
  occupation?: string;
  annual_income?: string | null;
  company_type?: string;
  incorporation_date?: string | null;
  cin_number?: string;
  nature_of_business?: string;
  annual_turnover?: string | null;
  num_employees?: number | null;
  website?: string;
  pan_number?: string;
  aadhaar_number?: string;
  gst_number?: string;
  passport_number?: string;
  driving_license?: string;
  address_line1?: string;
  address_line2?: string;
  landmark?: string;
  /** The city as typed; `Lead.city` is a FK that drops unknown names. */
  city_name?: string;
}

/** Everything the lead form can send. Lead columns + `contact_details`. */
export interface LeadWritePayload {
  salutation?: string;
  first_name: string;
  last_name?: string;
  email?: string;
  phone: string;
  alternate_phone?: string;
  whatsapp_number?: string;
  company_name?: string;
  designation?: string;
  industry?: string;
  city?: string;
  state?: string;
  pincode?: string;
  source?: string;
  campaign?: string;
  product_interest?: string;
  estimated_premium?: string;
  priority?: string;
  notes?: string;
  /** Item 9: when to call this lead back. ISO 8601; the form sends the
   *  browser-local value a `datetime-local` input produces. */
  next_follow_up?: string | null;
  /** The remarks that go with it -- what to say, or what was promised. Both
   *  columns have existed since `0002`; `LeadCreateSerializer` simply did not
   *  list them, so a follow-up typed on the Add Lead form went nowhere. */
  follow_up_notes?: string;
  contact_details?: LeadContactDetails;
}

/**
 * The lead as the DETAIL endpoint actually returns it. `types/index.ts` (owned
 * by another workstream this batch) still describes the older, narrower shape;
 * `LeadDetailSerializer` has always sent these too.
 */
export type LeadDetail = Lead & {
  salutation?: string | null;
  whatsapp_number?: string | null;
  industry?: string | null;
  city?: string | null;
  city_name?: string | null;
  state?: string | null;
  pincode?: string | null;
  contact_details?: LeadContactDetails;
};

/** `POST /leads/<id>/convert/`. */
export interface LeadConvertResponse {
  message: string;
  customer_id: string;
  /** false when an existing same-broker customer was LINKED, not created. */
  customer_created: boolean;
  lead: LeadDetail;
}

export type LeadBulkConvertSkipReason =
  | 'not_found'
  | 'already_converted'
  | 'no_contact_details'
  | 'customer_not_in_broker'
  | 'error';

/** `POST /leads/bulk_convert/`. Every requested id lands in exactly one list. */
export interface LeadBulkConvertResult {
  converted: Array<{ id: string; customer_id: string; customer_created: boolean }>;
  skipped: Array<{ id: string; reason: LeadBulkConvertSkipReason | string }>;
}

/** This lead, shaped for the Add Policy form.
 *
 *  Make/model/variant come back as MASTER IDS where the lead's free text
 *  resolved, and as `''` where it did not — with the original text alongside,
 *  so the form can show what was said next to a blank select. An unresolved
 *  variant is deliberately left for the broker: guessing one reprices the
 *  policy. */
export interface LeadPolicyPrefill {
  lead_id: string;
  lead_number: string;
  customer: string;
  holder: Record<string, string>;
  vehicle: Record<string, string>;
  /** What the enquiry was ABOUT: the insurance type and company the lead
   *  already recorded, as the Add Policy form's own ids.
   *
   *  Same contract as `vehicle` — `insurance_type` is an `InsuranceType` id
   *  where `Lead.product_interest` matched one on `code`, and `''` where it
   *  did not, with the human label in `insurance_type_label` so the form can
   *  show what the lead said next to a blank select. A wrong classification
   *  silently changes which commission rate card the policy is priced
   *  against, so the server will not guess one. */
  cover?: LeadPolicyPrefillCover;
  /** B15 -- New or Renewal, computed server-side from the lead's own
   *  ``is_new_vehicle`` / ``existing_policy_expiry`` columns so the quote
   *  wizard's Business Type step does not have to re-derive it. `''` when
   *  the lead recorded neither signal -- the broker picks on the step this
   *  prefill lands them on. */
  business_type: 'new' | 'renewal' | '';
}

/** The `cover` block of `GET /leads/<id>/policy-prefill/`. Every value is a
 *  string; an unresolved one is `''`, never null — the prefill patches only
 *  truthy values, and a null would land in a select as the text "null". */
export interface LeadPolicyPrefillCover {
  insurance_type: string;
  insurance_type_label: string;
  insurer: string;
  insurer_name: string;
  product: string;
  product_name: string;
}

/** Someone a lead can be assigned to. */
export interface AssignableUser {
  id: string;
  full_name: string;
  email: string;
  role: string;
}

export const leadService = {
  /** Who this broker may assign a lead to. Server-scoped to the caller's own
   *  broker — the assign endpoints resolve the id against this same set, so a
   *  hand-typed id from another tenant is refused. */
  async getAssignableUsers(): Promise<AssignableUser[]> {
    const response = await api.get<AssignableUser[]>('/leads/assignable-users/');
    return response.data;
  },

  async bulkAssignLeads(leadIds: string[], assignedTo: string): Promise<{ assigned_count: number }> {
    const response = await api.post<{ assigned_count: number }>('/leads/bulk_assign/', {
      lead_ids: leadIds,
      assigned_to: assignedTo,
    });
    return response.data;
  },

  async getPolicyPrefill(id: string): Promise<LeadPolicyPrefill> {
    const response = await api.get<LeadPolicyPrefill>(`/leads/${id}/policy-prefill/`);
    return response.data;
  },

  async getLeads(page: number = 1, filters?: LeadFilters): Promise<PaginatedResponse<Lead>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());

    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.source) params.append('source', filters.source);
    if (filters?.campaign) params.append('campaign', filters.campaign);
    if (filters?.assigned_to) params.append('assigned_to', filters.assigned_to);
    if (filters?.date_from) params.append('created_at_after', filters.date_from);
    if (filters?.date_to) params.append('created_at_before', filters.date_to);
    if (filters?.ordering) params.append('ordering', filters.ordering);

    const response = await api.get<PaginatedResponse<Lead>>(`/leads/?${params.toString()}`);
    return response.data;
  },

  async getLead(id: string): Promise<LeadDetail> {
    const response = await api.get<LeadDetail>(`/leads/${id}/`);
    return response.data;
  },

  /** B15 -- the contact's own MOST RECENT converted lead, the one "Create New
   *  Quote" prefills the wizard from.
   *
   *  The list endpoint already filters on `converted_to_customer` (item 10),
   *  but its serializer carries no `converted_at` to sort by, so "most
   *  recent" is resolved in two steps: filter the list (cheap, and almost
   *  always the whole answer -- a contact has zero or one converted lead),
   *  then only for the rare contact with several, fetch each one's DETAIL
   *  (which does carry `converted_at`) and rank by that, falling back to
   *  `created_at` for two converted without a timestamp of their own.
   *
   *  `null` for a contact with no converted lead. Throws on a failed
   *  request -- callers that must never let a broken lookup block "new
   *  quote" catch it themselves, same as the renewal/enquiry prefills. */
  async getMostRecentConvertedLead(customerId: string): Promise<LeadDetail | null> {
    const response = await api.get<PaginatedResponse<Lead>>('/leads/', {
      params: { converted_to_customer: customerId, page_size: 25, ordering: '-created_at' },
    });
    const rows = Array.isArray(response.data?.results) ? response.data.results : [];
    if (rows.length === 0) return null;
    if (rows.length === 1) return leadService.getLead(rows[0].id);

    const details = await Promise.all(rows.map((row) => leadService.getLead(row.id)));
    const rank = (l: LeadDetail) => {
      const stamp = l.converted_at || l.created_at;
      return stamp ? new Date(stamp).getTime() : 0;
    };
    return details.reduce((best, l) => (rank(l) > rank(best) ? l : best));
  },

  async createLead(data: LeadWritePayload | LeadCreateRequest): Promise<Lead> {
    const response = await api.post<Lead>('/leads/', data);
    return response.data;
  },

  async updateLead(id: string, data: Partial<LeadWritePayload>): Promise<Lead> {
    const response = await api.patch<Lead>(`/leads/${id}/`, data);
    return response.data;
  },

  /** Moves a lead along the pipeline. `won` is not offered from the UI: it runs
   *  the conversion, which has its own Convert button. */
  async updateLeadStatus(id: string, status: string): Promise<{ message: string; lead: Lead }> {
    const response = await api.post<{ message: string; lead: Lead }>(
      `/leads/${id}/update_status/`,
      { status },
    );
    return response.data;
  },

  async deleteLead(id: string): Promise<void> {
    await api.delete(`/leads/${id}/`);
  },

  /** Assign a lead to one of this broker's own staff.
   *
   *  The endpoint answers `{message, lead}` and never a bare `Lead` — the old
   *  `Promise<Lead>` return type was wrong in the same way `convertLead`'s was
   *  (see the note below it), so every field read off the result was
   *  undefined. */
  async assignLead(id: string, userId: string, notes?: string): Promise<{ message: string; lead: Lead }> {
    const response = await api.post<{ message: string; lead: Lead }>(
      `/leads/${id}/assign/`,
      { assigned_to: userId, notes: notes ?? '' },
    );
    return response.data;
  },

  // The endpoint has never returned a bare Lead: it answers with the customer
  // it created or linked. Typing it as `Lead` is why LeadDetailPage could not
  // navigate to the new customer.
  async convertLead(id: string): Promise<LeadConvertResponse> {
    const response = await api.post<LeadConvertResponse>(`/leads/${id}/convert/`);
    return response.data;
  },

  /** #273: convert many leads at once. Broker-scoped and idempotent server-side. */
  async bulkConvertLeads(leadIds: string[]): Promise<LeadBulkConvertResult> {
    const response = await api.post<LeadBulkConvertResult>('/leads/bulk_convert/', {
      lead_ids: leadIds,
    });
    return response.data;
  },

  /** The lead's activity timeline.
   *
   *  This endpoint is PAGINATED (`LeadActivityViewSet.pagination_class`), so
   *  it answers `{count, ..., results}`. This used to cast that envelope to
   *  `LeadActivity[]` and return it whole: `activities.length` was therefore
   *  `undefined`, `undefined > 0` is false, and the timeline rendered "No
   *  activities recorded yet." no matter how many activities the lead had.
   *  The cast is what hid it from the type checker. `getSources` and
   *  `getCampaigns` below have always unwrapped correctly.
   *
   *  An unpaginated array is accepted too, so turning pagination off would
   *  not silently empty the timeline again; anything else THROWS rather than
   *  resolving to nothing, because "the request failed" and "there are none"
   *  must not look the same on screen. */
  async getActivities(leadId: string): Promise<LeadActivity[]> {
    const response = await api.get<PaginatedResponse<LeadActivity> | LeadActivity[]>(
      `/leads/${leadId}/activities/`,
    );
    const body = response.data;
    if (Array.isArray(body)) return body;
    if (body && Array.isArray(body.results)) return body.results;
    throw new Error('The activity timeline came back in a shape this page cannot read.');
  },

  async addActivity(leadId: string, data: Partial<LeadActivity>): Promise<LeadActivity> {
    const response = await api.post<LeadActivity>(`/leads/${leadId}/activities/`, data);
    return response.data;
  },

  async getSources(): Promise<LeadSource[]> {
    const response = await api.get<PaginatedResponse<LeadSource>>('/leads/sources/');
    return response.data.results;
  },

  async getCampaigns(): Promise<LeadCampaign[]> {
    const response = await api.get<PaginatedResponse<LeadCampaign>>('/leads/campaigns/');
    return response.data.results;
  },
};

/** B15 -- where a contact's "Create New Quote" button should go.
 *
 * Plain `/quotes/new?customer=<id>` when the contact has no converted lead
 * (or the lookup itself fails); `&lead=<id>&step=business_type` added on top
 * when it does, so the wizard opens on Business Type with its fields already
 * filled from the lead. The lookup failing is never allowed to block the
 * ordinary "new quote" action -- it falls back to the plain link exactly as
 * if there were no lead at all. Shared by `ContactQuickActions` and
 * `CustomerDetailPage`'s own "Create New Quote" button so the two never pick
 * a different lead for the same contact. */
export async function resolveNewQuoteHref(customerId: string): Promise<string> {
  try {
    const lead = await leadService.getMostRecentConvertedLead(customerId);
    if (lead) {
      return `/quotes/new?customer=${customerId}&lead=${lead.id}&step=business_type`;
    }
  } catch {
    // fall through -- see the doc comment above.
  }
  return `/quotes/new?customer=${customerId}`;
}
